import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from '@/components/utils/translations';
import { isSysExamProductContextTenant } from '@/lib/accountTargetProducts';
import { isFeedbackfruitsFamilyTenant, isDemarqueFamilyTenant, isBibliothecaFamilyTenant } from '@/lib/tenantUtils';

/** Legacy fallback when the market module is off. Inputs are the dead accounts.*_vendor columns — V2 tenants use MarketAccountTechStack (inst_vendor_stack). */
export default function TechStack({ account, currentTenant }) {
  const { t } = useTranslation();
  const { 
    lms_vendor,
    assessment_vendor,
    sis_vendor, 
    crm_vendor, 
    eportfolio_vendor, 
    proctoring_vendor, 
    feedback_vendor,
    tutor_vendor,
    support_vendor,
    library_product,
    library_vendor,
    ifp_product,
    ifp_vendor,
    integrator_product,
    integrator_vendor,
    immersive_vendor,
    authoring_product,
    authoring_vendor,
    ehr_product,
    ehr_vendor,
    ipass_product,
    ipass_vendor,
    patientportal_product,
    patientportal_vendor,
    admissions_product,
    admissions_vendor,
    home_product,
    home_vendor,
    tower_product,
    tower_vendor,
    rttv_product,
    rttv_vendor,
    wms_product,
    wms_vendor,
    evaluation_vendor,
    omr_scanning_vendor,
    integrity_vendor,
    research_vendor,
    student_success_vendor,
    room_event_scheduling_vendor,
    curriculum_catalog_vendor,
    degree_planning_vendor,
    course_demand_planning_vendor,
    faculty_workload_vendor,
    academic_scheduling_vendor,
    attendance_vendor,
    tenant_id: accountTenantId 
  } = account || {};
  
  const tenant_id = currentTenant || accountTenantId;
  const isDemarque = isDemarqueFamilyTenant(tenant_id);
  const isBibliotheca = isBibliothecaFamilyTenant(tenant_id);
  const isEdusign = tenant_id === 'edusign';
  const isSysExamTenant = isSysExamProductContextTenant(tenant_id);
  const isLearnwise = tenant_id === 'learnwise';
  const isEdqwest = tenant_id === 'edqwest';
  const isFaria = tenant_id === 'faria';
  const isCalmedica = tenant_id === 'calmedica';
  const isKairadigital = tenant_id === 'kairadigital';
  const isProwise = tenant_id === 'prowise';
  const isSkillwell = tenant_id === 'skillwell';
  const isAlmentor = tenant_id === 'almentor';
  const isInfonetica = tenant_id === 'infonetica';
  const isCayuse = tenant_id === 'cayuse';
  const isGravyty = tenant_id === 'gravyty';
  const isUplanner = tenant_id === 'uplanner';
  const isLiaison = tenant_id === 'liaison';
  const isRumi = tenant_id === 'rumi';
  const isFeedbackfruits = isFeedbackfruitsFamilyTenant(tenant_id);
  const isScantron = tenant_id === 'scantron';
  
  // Helper to prefer vendor over product field
  const getField = (vendorField, productField) => vendorField || productField;
  
  // Build tech stack based on tenant
  const tech = [];
  const addStandardTech = () => {
    if (lms_vendor) tech.push({ label: 'LMS', value: lms_vendor });
    if (assessment_vendor) tech.push({ label: 'Assessment', value: assessment_vendor });
    if (getField(ifp_vendor, ifp_product)) tech.push({ label: 'IFP', value: getField(ifp_vendor, ifp_product), tooltip: 'Interactive flat panel' });
    if (getField(integrator_vendor, integrator_product)) tech.push({ label: 'Integrator', value: getField(integrator_vendor, integrator_product), tooltip: 'Integrator of technology' });
    if (immersive_vendor) tech.push({ label: 'Immersive', value: immersive_vendor, tooltip: 'Immersive technology vendor' });
    if (getField(authoring_vendor, authoring_product)) tech.push({ label: 'Authoring', value: getField(authoring_vendor, authoring_product), tooltip: 'Content authoring product' });
    if (sis_vendor) tech.push({ label: 'SIS', value: sis_vendor });
    if (crm_vendor) tech.push({ label: 'CRM', value: crm_vendor });
    if (eportfolio_vendor) tech.push({ label: 'ePortfolio', value: eportfolio_vendor });
    if (proctoring_vendor) tech.push({ label: 'Proctoring', value: proctoring_vendor });
  };
  
  if (isBibliotheca) {
    if (getField(library_vendor, library_product)) {
      tech.push({ label: 'Library systems', value: getField(library_vendor, library_product) });
    }
  } else if (isDemarque) {
    // Library vendors: library_vendor should appear in Tech Stack area
    if (getField(library_vendor, library_product)) {
      tech.push({ label: 'Library', value: getField(library_vendor, library_product) });
    }
  } else if (isEdqwest) {
    // Edqwest: Admissions_vendor should appear right next to sis_vendor
    if (lms_vendor) tech.push({ label: 'LMS', value: lms_vendor });
    if (assessment_vendor) tech.push({ label: 'Assessment', value: assessment_vendor });
    if (sis_vendor) tech.push({ label: 'SIS', value: sis_vendor });
    if (getField(admissions_vendor, admissions_product)) tech.push({ label: 'Admissions', value: getField(admissions_vendor, admissions_product) });
    if (crm_vendor) tech.push({ label: 'CRM', value: crm_vendor });
  } else if (isFaria) {
    // Faria: Admissions_vendor and home_vendor should appear right next to sis_vendor
    if (lms_vendor) tech.push({ label: 'LMS', value: lms_vendor });
    if (assessment_vendor) tech.push({ label: 'Assessment', value: assessment_vendor });
    if (sis_vendor) tech.push({ label: 'SIS', value: sis_vendor });
    if (getField(admissions_vendor, admissions_product)) tech.push({ label: 'Admissions', value: getField(admissions_vendor, admissions_product) });
    if (getField(home_vendor, home_product)) tech.push({ label: 'Home', value: getField(home_vendor, home_product), tooltip: 'School-to-home management system' });
    if (crm_vendor) tech.push({ label: 'CRM', value: crm_vendor });
  } else if (isCalmedica) {
    // Calmedica: ehr_vendor, ipass_vendor, patientportal_vendor should appear
    if (crm_vendor) tech.push({ label: 'CRM', value: crm_vendor });
    if (getField(ehr_vendor, ehr_product)) tech.push({ label: 'EHR', value: getField(ehr_vendor, ehr_product) });
    if (getField(ipass_vendor, ipass_product)) tech.push({ label: 'iPass', value: getField(ipass_vendor, ipass_product) });
    if (getField(patientportal_vendor, patientportal_product)) tech.push({ label: 'Patient Portal', value: getField(patientportal_vendor, patientportal_product) });
  } else if (isProwise) {
    // Prowise: integrator_vendor and ifp_vendor should appear next to sis_vendor
    if (lms_vendor) tech.push({ label: 'LMS', value: lms_vendor });
    if (assessment_vendor) tech.push({ label: 'Assessment', value: assessment_vendor });
    if (sis_vendor) tech.push({ label: 'SIS', value: sis_vendor });
    if (getField(integrator_vendor, integrator_product)) tech.push({ label: 'Integrator', value: getField(integrator_vendor, integrator_product), tooltip: 'Integrator of technology' });
    if (getField(ifp_vendor, ifp_product)) tech.push({ label: 'IFP', value: getField(ifp_vendor, ifp_product), tooltip: 'Interactive flat panel' });
    if (crm_vendor) tech.push({ label: 'CRM', value: crm_vendor });
    if (eportfolio_vendor) tech.push({ label: 'ePortfolio', value: eportfolio_vendor });
    if (proctoring_vendor) tech.push({ label: 'Proctoring', value: proctoring_vendor });
  } else if (isSkillwell) {
    // Skillwell: authoring_vendor and immersive_vendor should appear next to sis_vendor
    if (lms_vendor) tech.push({ label: 'LMS', value: lms_vendor });
    if (assessment_vendor) tech.push({ label: 'Assessment', value: assessment_vendor });
    if (sis_vendor) tech.push({ label: 'SIS', value: sis_vendor });
    if (getField(authoring_vendor, authoring_product)) tech.push({ label: 'Authoring', value: getField(authoring_vendor, authoring_product), tooltip: 'Content authoring product' });
    if (immersive_vendor) tech.push({ label: 'Immersive', value: immersive_vendor, tooltip: 'Immersive technology vendor' });
    if (crm_vendor) tech.push({ label: 'CRM', value: crm_vendor });
    if (eportfolio_vendor) tech.push({ label: 'ePortfolio', value: eportfolio_vendor });
    if (proctoring_vendor) tech.push({ label: 'Proctoring', value: proctoring_vendor });
  } else if (isKairadigital) {
    // Kairadigital: rttv_vendor, tower_vendor, wms_vendor should appear next to sis_vendor
    if (sis_vendor) tech.push({ label: 'SIS', value: sis_vendor });
    if (getField(rttv_vendor, rttv_product)) tech.push({ label: 'RTTV', value: getField(rttv_vendor, rttv_product), tooltip: 'RTTV / Visibility' });
    if (getField(tower_vendor, tower_product)) tech.push({ label: 'Tower', value: getField(tower_vendor, tower_product), tooltip: 'Planning / Control Tower' });
    if (getField(wms_vendor, wms_product)) tech.push({ label: 'WMS', value: getField(wms_vendor, wms_product), tooltip: 'Warehouse Management System' });
  } else if (isLearnwise) {
    // Learnwise: lms_vendor, feedback_vendor, tutor_vendor, support_vendor
    if (lms_vendor) tech.push({ label: 'LMS', value: lms_vendor });
    if (feedback_vendor) tech.push({ label: 'Feedback', value: feedback_vendor, tooltip: 'Feedback / grading platform' });
    if (tutor_vendor) tech.push({ label: 'Tutor', value: tutor_vendor, tooltip: 'Tutoring / in-course support' });
    if (support_vendor) tech.push({ label: 'Support', value: support_vendor, tooltip: 'Student support / help desk' });
  } else if (isGravyty) {
    // Gravyty: same vendor set as Learnwise
    if (lms_vendor) tech.push({ label: 'LMS', value: lms_vendor });
    if (sis_vendor) tech.push({ label: 'SIS', value: sis_vendor });
    if (feedback_vendor) tech.push({ label: 'Feedback', value: feedback_vendor, tooltip: 'Feedback / grading platform' });
    if (tutor_vendor) tech.push({ label: 'Tutor', value: tutor_vendor, tooltip: 'Tutoring / in-course support' });
    if (support_vendor) tech.push({ label: 'Support', value: support_vendor, tooltip: 'Student support / help desk' });
  } else if (isEdusign) {
    if (lms_vendor) tech.push({ label: 'LMS', value: lms_vendor });
    if (sis_vendor) tech.push({ label: 'SIS', value: sis_vendor });
    if (attendance_vendor) {
      tech.push({
        label: 'Attendance',
        value: attendance_vendor,
        tooltip: 'Attendance management system',
      });
    }
  } else if (isSysExamTenant) {
    // Evasys / Scantron: evaluation_vendor and standard tech stack
    if (lms_vendor) tech.push({ label: 'LMS', value: lms_vendor });
    if (assessment_vendor) tech.push({ label: 'Assessment', value: assessment_vendor });
    if (sis_vendor) tech.push({ label: 'SIS', value: sis_vendor });
    if (evaluation_vendor) tech.push({ label: 'Evaluation', value: evaluation_vendor, tooltip: 'Evaluation vendor' });
    if (isScantron && omr_scanning_vendor) {
      tech.push({
        label: 'OMR Scanning',
        value: omr_scanning_vendor,
        tooltip: 'OMR scanning vendor',
      });
    }
    if (crm_vendor) tech.push({ label: 'CRM', value: crm_vendor });
    if (eportfolio_vendor) tech.push({ label: 'ePortfolio', value: eportfolio_vendor });
    if (proctoring_vendor) tech.push({ label: 'Proctoring', value: proctoring_vendor });
  } else if (isCayuse) {
    // Cayuse: research, LMS, SIS only
    if (research_vendor) tech.push({ label: 'Research', value: research_vendor, tooltip: 'Research vendor' });
    if (lms_vendor) tech.push({ label: 'LMS', value: lms_vendor });
    if (sis_vendor) tech.push({ label: 'SIS', value: sis_vendor });
  } else if (isInfonetica) {
    // Infonetica: standard tech stack + Learnwise/Gravyty vendor set + research vendor
    addStandardTech();
    if (feedback_vendor) tech.push({ label: 'Feedback', value: feedback_vendor, tooltip: 'Feedback / grading platform' });
    if (tutor_vendor) tech.push({ label: 'Tutor', value: tutor_vendor, tooltip: 'Tutoring / in-course support' });
    if (support_vendor) tech.push({ label: 'Support', value: support_vendor, tooltip: 'Student support / help desk' });
    if (research_vendor) tech.push({ label: 'Research', value: research_vendor, tooltip: 'Research vendor' });
  } else if (isAlmentor) {
    // Almentor: standard tech stack + library vendor
    addStandardTech();
    if (getField(library_vendor, library_product)) tech.push({ label: 'Library', value: getField(library_vendor, library_product) });
  } else if (isRumi) {
    // Rumi: academic integrity focus — integrity + proctoring alongside core stack
    if (lms_vendor) tech.push({ label: 'LMS', value: lms_vendor });
    if (assessment_vendor) tech.push({ label: 'Assessment', value: assessment_vendor });
    if (sis_vendor) tech.push({ label: 'SIS', value: sis_vendor });
    if (crm_vendor) tech.push({ label: 'CRM', value: crm_vendor });
    if (eportfolio_vendor) tech.push({ label: 'ePortfolio', value: eportfolio_vendor });
    if (integrity_vendor) {
      tech.push({
        label: 'Integrity',
        value: integrity_vendor,
        tooltip: 'Academic integrity / plagiarism detection',
      });
    }
    if (proctoring_vendor) tech.push({ label: 'Proctoring', value: proctoring_vendor });
  } else if (isFeedbackfruits) {
    // FeedbackFruits: core stack plus feedback / grading platform when present
    addStandardTech();
    if (feedback_vendor) {
      tech.push({
        label: 'Feedback',
        value: feedback_vendor,
        tooltip: 'Feedback / grading platform',
      });
    }
  } else if (isLiaison) {
    // Liaison: LMS, SIS, Admissions only (no ePortfolio / CRM / etc.)
    if (lms_vendor) tech.push({ label: 'LMS', value: lms_vendor });
    if (sis_vendor) tech.push({ label: 'SIS', value: sis_vendor });
    if (getField(admissions_vendor, admissions_product)) {
      tech.push({ label: 'Admissions', value: getField(admissions_vendor, admissions_product) });
    }
  } else if (isUplanner) {
    // uPlanner: standard stack without ePortfolio (market share uses degree_planning_vendor); plus HE planning/scheduling vendors
    addStandardTech();
    for (let i = tech.length - 1; i >= 0; i -= 1) {
      if (tech[i].label === 'ePortfolio') tech.splice(i, 1);
    }
    if (student_success_vendor) tech.push({ label: 'Student Success', value: student_success_vendor });
    if (room_event_scheduling_vendor) tech.push({ label: 'Room & Event Scheduling', value: room_event_scheduling_vendor });
    if (curriculum_catalog_vendor) tech.push({ label: 'Curriculum / Catalog', value: curriculum_catalog_vendor });
    if (degree_planning_vendor) tech.push({ label: 'Degree Planning', value: degree_planning_vendor });
    if (course_demand_planning_vendor) tech.push({ label: 'Course Demand Planning', value: course_demand_planning_vendor });
    if (faculty_workload_vendor) tech.push({ label: 'Faculty Workload', value: faculty_workload_vendor });
    if (academic_scheduling_vendor) tech.push({ label: 'Academic Scheduling', value: academic_scheduling_vendor });
  } else {
    // Standard products (shown for instructure, smowl, etc.)
    addStandardTech();
  }

  if (integrity_vendor && !isRumi) {
    tech.push({
      label: 'Integrity',
      value: integrity_vendor,
      tooltip: 'Academic integrity / plagiarism detection',
    });
  }
  
  const filteredTech = tech.filter(item => item.value && item.value.toLowerCase() !== 'none' && item.value.toLowerCase() !== 'n/a');
  const dedupedTech = filteredTech.filter((item, index, arr) => {
    const key = `${item.label}::${String(item.value).trim().toLowerCase()}`;
    return index === arr.findIndex((candidate) => `${candidate.label}::${String(candidate.value).trim().toLowerCase()}` === key);
  });
    
  const hasTech = dedupedTech.length > 0;

  return (
    <Card className="border-0 shadow-md">
      <CardContent className="p-3 md:p-4">
        <p className="text-xs md:text-sm text-[#5C6B7A] text-center mb-2">{t('techStack')}</p>
        <div className="flex flex-wrap justify-center items-center gap-1 min-h-[40px] md:min-h-[48px]">
          {hasTech ? (
            dedupedTech.map((item, index) => (
              <TooltipProvider key={index}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="inline-flex">
                      <Badge variant="secondary" className="bg-gray-100 text-[#5C6B7A] text-[10px] md:text-xs cursor-help">
                        {item.value}
                      </Badge>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent 
                    className="bg-[#0A1A2F] text-white border-0 shadow-lg px-2 py-1 text-[10px]"
                    style={{ maxWidth: 'fit-content', minWidth: 'auto' }}
                  >
                    <p className="font-semibold text-[#00C2C2] whitespace-nowrap">
                      {item.tooltip || item.label}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))
          ) : (
            <p className="text-xs text-center text-[#5C6B7A]">{t('noTechData') || 'No specified tech data'}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}